import React, { useMemo, useRef } from "react"
import { Box, Link, Typography } from "@mui/material"
import SimpleMarkdown from "simple-markdown"
import { documentFromLink, DocumentType, headingId, LegalContent } from "./documents"

interface Props {
  content: LegalContent
  onOpenDocument: (type: DocumentType) => void
}

const astText = (nodes: SimpleMarkdown.ASTNode): string =>
  Array.isArray(nodes)
    ? nodes.map(astText).join("")
    : typeof nodes.content === "string" ? nodes.content : astText(nodes.content || [])

export const LegalDocument: React.FC<Props> = ({ content, onOpenDocument }) => {
  const root = useRef<HTMLDivElement>(null)
  const rendered = useMemo(() => {
    if (content.format !== "markdown") {
      return <Typography component="p" sx={{ whiteSpace: "pre-wrap" }}>{content.text}</Typography>
    }

    const link: SimpleMarkdown.ReactNodeOutput = (node, output, state) => {
      // The parser can recognize a URL inside an explicit link's label.
      // Render that inner URL as text, rather than nesting interactive anchors.
      if (state.insideLegalLink) return <React.Fragment key={state.key}>{output(node.content, state)}</React.Fragment>
      const href = SimpleMarkdown.sanitizeUrl(node.target) || undefined
      const documentType = href ? documentFromLink(href) : undefined
      return (
        <Link
          key={state.key}
          href={href}
          aria-label={documentType === "state-privacy-notices" ? "Individual State Privacy Notices" : undefined}
          data-automation-id={documentType ? `legal-document-${documentType}-link` : "legal-document-link"}
          onClick={event => {
            if (documentType) {
              event.preventDefault()
              onOpenDocument(documentType)
            } else if (href?.startsWith("#")) {
              const target = Array.from(root.current?.querySelectorAll<HTMLElement>("[id]") || [])
                .find(element => element.id === href.slice(1))
              if (target) {
                event.preventDefault()
                target.focus({ preventScroll: true })
                target.scrollIntoView({ block: "start" })
              }
            }
          }}
        >
          {output(node.content, { ...state, insideLegalLink: true })}
        </Link>
      )
    }

    const rules = {
      ...SimpleMarkdown.defaultRules,
      link: { ...SimpleMarkdown.defaultRules.link, react: link },
      url: { ...SimpleMarkdown.defaultRules.url, react: link },
      mailto: { ...SimpleMarkdown.defaultRules.mailto, react: link },
      paragraph: {
        ...SimpleMarkdown.defaultRules.paragraph,
        react: ((node, output, state) => (
          <Typography key={state.key} component="p" variant="body1">
            {output(node.content, state)}
          </Typography>
        )) as SimpleMarkdown.ReactNodeOutput
      },
      heading: {
        ...SimpleMarkdown.defaultRules.heading,
        react: ((node, output, state) => (
          <Typography
            key={state.key}
            component={`h${Math.min(6, Math.max(2, node.level))}` as "h2"}
            variant={node.level <= 2 ? "h3" : "h4"}
            id={headingId(astText(node.content))}
            tabIndex={-1}
          >
            {output(node.content, state)}
          </Typography>
        )) as SimpleMarkdown.ReactNodeOutput
      }
    }
    const parse = SimpleMarkdown.parserFor(rules)
    return SimpleMarkdown.outputFor(rules, "react")(parse(content.text + "\n\n", { inline: false }))
  }, [content, onOpenDocument])

  return (
    <Box
      ref={root}
      role="document"
      tabIndex={0}
      data-automation-id="legal-document-content"
      sx={{
        overflowWrap: "anywhere",
        "& p, & ul, & ol": { marginBottom: 4 },
        "& h2, & h3, & h4, & h5, & h6": { marginTop: 6, marginBottom: 3 },
        "& li": { marginBottom: 2 },
        "& ul, & ol": { paddingLeft: 6 }
      }}
    >
      {rendered}
    </Box>
  )
}
